import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, Divider,
    IconButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";

import { notify } from "../../../util/toast";
import { getApiErrorMessage } from "../../../util/apiFeedback";
import { approvalApi } from "../api/approvalApi";
import { channelApi } from "../../channel/api/channelApi";
import { formatSize } from "../approvalConstants";

import { useSelectableForms } from "./useSelectableForms";
import FormSidebar from "./FormSidebar";
import DynamicForm, { isVisible } from "./DynamicForm";
import ApprovalLineEditor from "./ApprovalLineEditor";

/**
 * 결재 상신 모달.
 *
 * <h3>왜 3단계 마법사를 버렸나</h3>
 * 예전에는 "양식 선택 → 내용 작성 → 결재선 설정" 세 화면을 순서대로 넘겼다. 양식이 7종일 때는
 * 괜찮았지만 19종이 되면서 첫 화면이 카드 스크롤로 길어졌고, 결재선을 보려면 작성 화면을
 * 떠나야 해서 "이 양식이 누구한테 가는지" 를 확인하며 쓸 수 없었다.
 *
 * 좌우 배치로 바꿨다 — 왼쪽에 양식 목록이 항상 떠 있고, 오른쪽에서 안내문구·입력칸·결재선을
 * 한 화면에서 본다. 양식을 바꿔가며 비교하기도 쉽다.
 *
 * <h3>양식은 서버에서 온다</h3>
 * 목록·입력칸·결재선·열람자 전부 {@code GET /api/approval/forms} 응답이다.
 * 하드코딩이 없으므로 관리자가 양식을 추가하면 배포 없이 여기 나타난다.
 */
/**
 * 결재 작성 — 그리고 상신 후 «수정».
 *
 * <p>{@code editing} 이 오면 그 문서를 고치는 모드다. 양식은 바꿀 수 없고(슬롯 구성이 달라지면
 * 이미 승격된 값이 갈 곳을 잃는다) 내용·결재선·열람자만 고친다. 저장하면 서버가 결재선을
 * 1단계부터 다시 돌린다 — 아무도 자기가 안 본 내용으로 승인한 게 되지 않게 하기 위해서다.
 */
export default function CreateApprovalModal({ open, onClose, onCreated, myInfo, editing }) {
    const isEdit = Boolean(editing);
    const { groups, loading, error } = useSelectableForms(open);

    const [selected, setSelected] = useState(null);   // 선택된 양식(서버 응답 객체)
    const [values, setValues] = useState({});          // fieldKey → 값
    const [title, setTitle] = useState("");
    const [approvers, setApprovers] = useState([]);     // 결재선 — 순서 그대로, 마지막이 최종 승인자
    const [viewers, setViewers]     = useState([]);     // 열람자
    const [pendingFiles, setPendingFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const [projectChannels, setProjectChannels] = useState([]);
    const [refApprovals, setRefApprovals] = useState([]);
    const fileInputRef = useRef();
    const bodyRef = useRef(null);   // 오른쪽 입력 영역 — 양식을 바꾸면 맨 위로 올린다

    useEffect(() => {
        if (!open) return;
        setSelected(null); setValues({}); setTitle("");
        setApprovers([]); setViewers([]); setPendingFiles([]);
    }, [open]);

    /* 수정 모드 — 문서의 현재 내용을 그대로 채워 넣는다.
       양식 목록(groups)이 도착해야 selected 를 세울 수 있어 groups 도 의존성에 둔다. */
    useEffect(() => {
        if (!open || !isEdit || !groups?.length) return;
        const code = editing.templateCode;
        const form = groups.flatMap((g) => g.forms || []).find((f) => f.code === code);
        if (form) setSelected(form);
        setTitle(editing.title || "");
        setValues(editing.values || editing.formValues || {});
        // 키는 «결재자» 스키마(defaultApprovers 와 동일: id · deptName)여야 한다.
        // 열람자 스키마(employeeId · dept)를 쓰면 전송 시 approvers.map(a => a.id) 가 undefined 를 내고,
        // JSON 직렬화에서 null 이 되어 서버가 findById(null) 로 500 을 낸다.
        // canSubmit 은 length 만 보므로 버튼도 안 막혔다 — 실제로 수정이 통째로 불가능했다.
        setApprovers((editing.lines || [])
            .slice()
            .sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0))
            .map((l) => ({ id: l.approverId, name: l.approverName,
                           position: l.approverPosition, deptName: l.approverDept })));
        // 열람자 키도 마찬가지다 — ApprovalLineEditor 는 칩 라벨에 v.deptName 을 읽는다.
        // dept 로 담으면 부서명만 조용히 사라져 «부서 없는 사람» 처럼 보인다(결재자 쪽과 같은 실수).
        // 읽는 쪽 키도 같이 맞춘다 — 응답(ApprovalViewerDto)은 deptName 이다. 결재선만 approverDept 라
        // 이름이 어긋나 있어서, 담는 키만 고치고 v.dept 로 읽으면 값은 여전히 undefined 다.
        setViewers((editing.viewers || [])
            .map((v) => ({ employeeId: v.employeeId, name: v.name,
                           position: v.position, deptName: v.deptName })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEdit, groups, editing]);

    // 프로젝트 — CHANNEL 타입 칸의 선택지.
    //
    // 내가 속한 채널(channelApi.list)이 아니라 «전체 프로젝트» 를 쓴다. 결재는 남의 팀 프로젝트
    // 비용도 올리기 때문이다 — 재무팀이 영상팀 프로젝트 지출을 결의하는 식. 이름·id 만 오고
    // 채널 내용은 여전히 멤버만 볼 수 있다.
    useEffect(() => {
        if (!open) return;
        channelApi.projectOptions()
            .then((res) => setProjectChannels(Array.isArray(res) ? res : (res?.data || [])))
            .catch(() => setProjectChannels([]));
    }, [open]);

    // APPROVAL_REF 타입 칸의 선택지 — 내가 올려 승인된 결재(대체휴무 적립 근거 등).
    useEffect(() => {
        if (!open) return;
        approvalApi.sent(0, 200)
            .then((res) => {
                const list = res?.content ?? (Array.isArray(res) ? res : []);
                setRefApprovals(list.filter((a) => a.status === "APPROVED"));
            })
            .catch(() => setRefApprovals([]));
    }, [open]);

    const context = useMemo(
        () => ({ myInfo, projectChannels, refApprovals }),
        [myInfo, projectChannels, refApprovals]
    );

    /**
     * 양식이 정한 기본 결재선 — 서버가 «결재자» 로 표시한 단계만 그대로 옮긴다.
     *
     * 어느 단계가 결재자인지는 서버가 정한다({@code step.approver}). 예전에는 여기서
     * «기안자 단계는 빼고 · 스킵된 건 빼고» 를 다시 판단했는데, 같은 규칙이 두 곳에 있으면
     * 서버가 규칙을 바꾸는 순간 화면에 보인 결재선과 실제 결재선이 조용히 어긋난다.
     */
    const defaultApprovers = (form) => {
        const out = [];
        for (const s of form?.defaultLine || []) {
            if (!s.approver || !s.employeeId) continue;
            if (out.some((x) => x.id === s.employeeId)) continue;
            out.push({ id: s.employeeId, name: s.name, position: s.position, deptName: s.deptName });
        }
        return out;
    };
    const defaultViewers = (form) => (form?.defaultViewers || []).map((v) => ({
        employeeId: v.employeeId, name: v.name, deptName: v.deptName,
    }));

    const handleSelect = (form) => {
        // 수정 모드에서는 양식을 바꿀 수 없다 — 사이드바가 목록 자체를 안 그리므로 여기까지 올 일이
        // 없지만, 남겨 둔다. 예전에는 이 자리에서 내용·결재선·열람자가 통째로 초기화되는데
        // 서버는 «원래» 양식 버전으로 저장해서, 스키마가 겹치는 양식 쌍(연차신청서↔대체휴무 등)은
        // 400 도 안 나고 조용히 다른 문서로 바뀌었다. 방어선은 화면 하나에만 두지 않는다.
        if (isEdit) return;
        setSelected(form);
        setValues(initialValues(form, myInfo));
        setApprovers(defaultApprovers(form));
        setViewers(defaultViewers(form));
        // 다른 양식을 고르면 칸 구성이 통째로 바뀐다. 스크롤이 그대로면 새 양식의 중간부터
        // 보이게 되어 첫 칸을 못 보고 지나친다.
        bodyRef.current?.scrollTo({ top: 0 });
    };

    /** 필수 칸이 다 찼는지 — 서버도 같은 검사를 하지만 버튼을 미리 막아 왕복을 줄인다. */
    const missing = useMemo(() => {
        if (!selected) return [];
        return (selected.fields || [])
            .filter((f) => f.required && f.type !== "DERIVED" && f.type !== "HIDDEN")
            // 조건이 안 맞아 화면에 없는 칸은 세지 않는다 — 안 그러면 «용도 상세» 처럼
            // 숨어 있는 칸 때문에 상신 버튼이 영영 안 풀린다. 서버 검증(isVisible)과 같은 규칙.
            .filter((f) => isVisible(f, values))
            .filter((f) => {
                const v = values[f.key];
                if (Array.isArray(v)) return v.length === 0;
                if (v && typeof v === "object") return !v.id && !v.name;
                return v === undefined || v === null || String(v).trim() === "";
            })
            .map((f) => f.label);
    }, [selected, values]);

    const approverIds = useMemo(() => approvers.map((a) => a.id), [approvers]);

    const canSubmit = !!selected && missing.length === 0 && approverIds.length > 0 && !submitting;

    const handleSubmit = async () => {
        if (!selected) return;
        if (approverIds.length === 0) {
            notify("결재자가 없습니다. 결재선을 확인해 주세요.", "warning");
            return;
        }
        setSubmitting(true);
        try {
            if (isEdit) {
                await approvalApi.updateSubmitted(editing.id, {
                    title: title.trim() || null,
                    approverIds,
                    viewerIds: viewers.map((v) => v.employeeId),
                    values,
                });
                for (const f of pendingFiles) await approvalApi.uploadAttachment(editing.id, f);
                notify("수정했습니다. 결재선이 처음부터 다시 진행됩니다.", "success");
                onCreated();
                onClose();
                return;
            }
            const res = await approvalApi.create({
                title: title.trim() || null,
                templateCode: selected.code,
                approverIds,
                viewerIds: viewers.map((v) => v.employeeId),
                values,
                // form 은 보내지 않는다.
                //
                // templateCode 경로에서는 서버가 이 값을 쓰지 않는다. 그런데 «과도기 호환» 이라며
                // {...values, type} 을 실어 보내면 컨트롤러가 그걸 레거시 ApprovalForm 하위 타입으로
                // 역직렬화하려다 400 을 낸다 — values 의 거래처·프로젝트는 {id, name} 객체인데
                // 레거시 DTO 의 같은 이름 필드는 String 이라 타입이 안 맞는다.
                // 실측: 지출·매출 계열 6종이 이것 때문에 화면에서 아예 상신되지 않았다.
            });
            const approvalId = (res?.data ?? res)?.id;
            if (!approvalId) throw new Error("결재 ID를 받지 못했습니다.");

            for (const f of pendingFiles) await approvalApi.uploadAttachment(approvalId, f);

            notify("결재가 올려졌습니다.", "success");
            onCreated();
            onClose();
        } catch (e) {
            notify(getApiErrorMessage(e, "결재 생성 실패"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    // maxWidth={false} + width 96vw — «lg»(1200px)로는 표(GRID) 칸이 있는 지출 양식에서
    // 열이 눌려 가로 스크롤이 생겼다. 넓은 화면을 그대로 쓰되 1800px 에서 멈춘다.
    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
                PaperProps={{ sx: {
                    width: "96vw", maxWidth: 1600,
                    height: "92vh", maxHeight: "92vh", overflow: "hidden",
                } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between"
                   sx={{ px: 2, py: 1.25, borderBottom: "1px solid #e2e8f0" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    {isEdit ? "결재 수정" : "결재 올리기"}
                </Typography>
                <IconButton size="small" onClick={onClose}><CloseRoundedIcon sx={{ fontSize: 20 }} /></IconButton>
            </Stack>

            <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                {/* 수정 모드는 양식을 고정한다. 이름은 selected 가 서기 전(목록 로딩 중)이나
                    중지된 양식이라 목록에 없을 때도 비지 않도록 문서가 들고 온 값으로 떨어뜨린다. */}
                <FormSidebar groups={groups} loading={loading}
                             selectedCode={selected?.code} onSelect={handleSelect}
                             lockedFormName={isEdit
                                 ? (selected?.name || editing?.templateName || editing?.templateCode)
                                 : undefined} />

                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                    {error && (
                        <Box sx={{ p: 3 }}>
                            <Typography sx={{ fontSize: 13, color: "#dc2626" }}>
                                양식 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
                            </Typography>
                        </Box>
                    )}

                    {/* 수정 모드에는 «왼쪽에서 고르세요» 가 거짓말이 된다 — 왼쪽에 목록이 없다.
                        양식이 중지되어 상신 목록에서 빠지면 여기 걸리므로 그 경우도 말해 준다. */}
                    {!error && !selected && (
                        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, color: "#94a3b8" }}>
                            <Typography sx={{ fontSize: 13 }}>
                                {!isEdit ? "왼쪽에서 결재 양식을 선택해 주세요."
                                    : loading ? "양식을 불러오는 중입니다."
                                        : "이 문서의 양식을 불러오지 못했습니다. 중지된 양식일 수 있습니다."}
                            </Typography>
                        </Stack>
                    )}

                    {selected && (
                        <>
                            <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{selected.name}</Typography>
                                    {(selected.capabilities || []).map((c) => (
                                        <Chip key={c} size="small" label={c}
                                              sx={{ fontSize: 10.5, height: 20, bgcolor: "#f1f5f9" }} />
                                    ))}
                                </Stack>
                                <TextField
                                    size="small" fullWidth placeholder="제목 (비우면 양식 이름으로 자동 생성)"
                                    value={title} onChange={(e) => setTitle(e.target.value)}
                                    InputProps={{ sx: { fontSize: 13 } }}
                                />
                            </Box>

                            <Box ref={bodyRef} sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
                                <DynamicForm form={selected} values={values}
                                             onChange={setValues} context={context} />

                                <Divider sx={{ my: 2 }} />

                                <ApprovalLineEditor
                                    form={selected}
                                    myInfo={myInfo}
                                    approvers={approvers}
                                    viewers={viewers}
                                    onChangeApprovers={setApprovers}
                                    onChangeViewers={setViewers}
                                    onReset={() => {
                                        setApprovers(defaultApprovers(selected));
                                        setViewers(defaultViewers(selected));
                                    }}
                                />

                                <Divider sx={{ my: 2 }} />

                                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                    <Button size="small" startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => fileInputRef.current?.click()}
                                            sx={{ fontSize: 12, color: "#475569" }}>
                                        파일 첨부
                                    </Button>
                                    <input type="file" hidden multiple ref={fileInputRef}
                                           onChange={(e) => {
                                               setPendingFiles((p) => [...p, ...Array.from(e.target.files || [])]);
                                               e.target.value = "";
                                           }} />
                                </Stack>
                                {pendingFiles.map((f, i) => (
                                    <Stack key={i} direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.5 }}>
                                        <InsertDriveFileRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                                        <Typography sx={{ fontSize: 12, flex: 1 }}>{f.name}</Typography>
                                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{formatSize(f.size)}</Typography>
                                        <IconButton size="small"
                                                    onClick={() => setPendingFiles((p) => p.filter((_, x) => x !== i))}>
                                            <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Box>

                            <Stack direction="row" alignItems="center" justifyContent="space-between"
                                   sx={{ px: 3, py: 1.5, borderTop: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                                <Typography sx={{ fontSize: 11.5, color: missing.length ? "#dc2626" : "#94a3b8" }}>
                                    {missing.length
                                        ? `필수 항목이 비었습니다 — ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? " 외" : ""}`
                                        : approverIds.length === 0
                                            ? "결재자가 없습니다. 결재선을 확인해 주세요."
                                            : `결재자 ${approverIds.length}명 · 열람자 ${(selected.defaultViewers || []).length}명`}
                                </Typography>
                                <Stack direction="row" gap={1}>
                                    <Button onClick={onClose} sx={{ fontSize: 13, color: "#64748b" }}>취소</Button>
                                    <Tooltip title={missing.length ? missing.join(", ") : ""} placement="top">
                                        <span>
                                            <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}
                                                    startIcon={submitting
                                                        ? <CircularProgress size={14} color="inherit" />
                                                        : <SendRoundedIcon sx={{ fontSize: 16 }} />}
                                                    sx={{ fontSize: 13 }}>
                                                상신
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </>
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}

/** 양식이 정한 기본값 — HIDDEN 칸과 오늘 날짜처럼 사람이 안 채워도 되는 것들. */
/**
 * 상신 화면을 열 때 미리 채워지는 값.
 *
 * 양식이 들고 있는 {@code defaultValue} 를 그대로 쓴다. 숨김 칸을 필수로 둔 양식은 이 값이 없으면
 * 아무도 못 올리므로 발행 점검이 미리 막는다.
 */
function initialValues(form, myInfo) {
    const v = {};
    for (const f of form.fields || []) {
        if (f.defaultValue !== undefined && f.defaultValue !== null && f.defaultValue !== "") {
            // «TODAY» 는 고정 문자열이 아니라 오늘 날짜로 푼다. 양식 정의에 날짜를 박아 두면
            // 그날 하루만 맞고 다음날부터 틀린 값이 기본으로 채워진다.
            v[f.key] = f.defaultValue === "TODAY"
                ? new Date().toISOString().slice(0, 10)
                : f.defaultValue;
        } else if (f.type === "HIDDEN" && f.slot === "LEAVE_KIND") {
            // 기본값이 양식으로 옮겨가기 전에 발행된 버전을 위한 폴백.
            v[f.key] = "COMP_DAY";
        }
        // 자동 채움 칸은 값도 같이 넣어야 서버에 저장된다 — 화면에만 보이면 formData 가 빈다.
        if (f.type === "SUBMITTER")      v[f.key] = myInfo?.name || "";
        if (f.type === "SUBMITTER_DEPT") v[f.key] = myInfo?.deptName || "";
        if (f.key === "writtenAt" || f.key === "requestedAt") v[f.key] = new Date().toISOString().slice(0, 10);
    }
    return v;
}

